import React from "react";
import { FieldError } from "react-hook-form";
import { cn } from "../../utils";

export type SelectOption = {
  label: string;
  value: string | number;
};

const labelClass = "mb-1 block text-sm font-medium text-slate-700";
const controlClass =
  "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:bg-slate-50 disabled:text-slate-400";

export const FieldErrorText: React.FC<{ error?: FieldError }> = ({ error }) => {
  if (!error?.message) return null;
  return <p className="mt-1 text-xs text-red-600">{error.message}</p>;
};

type BaseFieldProps = {
  label: string;
  required?: boolean;
  error?: FieldError;
  className?: string;
};

export const TextInput = React.forwardRef<
  HTMLInputElement,
  BaseFieldProps & React.InputHTMLAttributes<HTMLInputElement>
>(({ label, required, error, className, id, ...props }, ref) => {
  const inputId = id ?? props.name;
  return (
    <div className={cn("flex flex-col", className)}>
      <label htmlFor={inputId} className={labelClass}>
        {label}
        {required ? <span className="text-red-500"> *</span> : null}
      </label>
      <input
        ref={ref}
        id={inputId}
        className={cn(controlClass, error && "border-red-400")}
        {...props}
      />
      <FieldErrorText error={error} />
    </div>
  );
});
TextInput.displayName = "TextInput";

export const TextArea = React.forwardRef<
  HTMLTextAreaElement,
  BaseFieldProps & React.TextareaHTMLAttributes<HTMLTextAreaElement>
>(({ label, required, error, className, id, ...props }, ref) => {
  const inputId = id ?? props.name;
  return (
    <div className={cn("flex flex-col", className)}>
      <label htmlFor={inputId} className={labelClass}>
        {label}
        {required ? <span className="text-red-500"> *</span> : null}
      </label>
      <textarea
        ref={ref}
        id={inputId}
        rows={3}
        className={cn(controlClass, "resize-y", error && "border-red-400")}
        {...props}
      />
      <FieldErrorText error={error} />
    </div>
  );
});
TextArea.displayName = "TextArea";

export const SelectInput = React.forwardRef<
  HTMLSelectElement,
  BaseFieldProps &
    React.SelectHTMLAttributes<HTMLSelectElement> & {
      options: SelectOption[];
      placeholder?: string;
    }
>(
  (
    {
      label,
      required,
      error,
      className,
      id,
      options,
      placeholder = "Επιλέξτε...",
      ...props
    },
    ref,
  ) => {
    const inputId = id ?? props.name;
    return (
      <div className={cn("flex flex-col", className)}>
        <label htmlFor={inputId} className={labelClass}>
          {label}
          {required ? <span className="text-red-500"> *</span> : null}
        </label>
        <select
          ref={ref}
          id={inputId}
          className={cn(controlClass, error && "border-red-400")}
          {...props}
        >
          <option value="">{placeholder}</option>
          {options.map((opt) => (
            <option key={String(opt.value)} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <FieldErrorText error={error} />
      </div>
    );
  },
);
SelectInput.displayName = "SelectInput";

export const FileInput = React.forwardRef<
  HTMLInputElement,
  BaseFieldProps &
    Omit<React.InputHTMLAttributes<HTMLInputElement>, "type" | "onChange"> & {
      onFileChange: (file: File | null) => void;
    }
>(({ label, required, error, className, id, onFileChange, ...props }, ref) => {
  const inputId = id ?? props.name;
  return (
    <div className={cn("flex flex-col", className)}>
      <label htmlFor={inputId} className={labelClass}>
        {label}
        {required ? <span className="text-red-500"> *</span> : null}
      </label>
      <input
        ref={ref}
        id={inputId}
        type="file"
        className={cn(controlClass, error && "border-red-400")}
        onChange={(e) => onFileChange(e.target.files?.[0] ?? null)}
        {...props}
      />
      <FieldErrorText error={error} />
    </div>
  );
});
FileInput.displayName = "FileInput";

export const MultiCheckboxGroup: React.FC<{
  label: string;
  options: SelectOption[];
  values: number[];
  onChange: (next: number[]) => void;
  error?: FieldError;
}> = ({ label, options, values, onChange, error }) => {
  return (
    <div className="flex flex-col">
      <span className={labelClass}>{label}</span>
      <div className="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-slate-200 bg-white p-2">
        {options.length === 0 ? (
          <p className="px-1 py-2 text-xs text-slate-400">
            Δεν υπάρχουν διαθέσιμες επιλογές.
          </p>
        ) : (
          options.map((opt) => {
            const numVal = Number(opt.value);
            const checked = values.includes(numVal);
            return (
              <label
                key={String(opt.value)}
                className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-slate-50"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => {
                    onChange(
                      e.target.checked
                        ? [...values, numVal]
                        : values.filter((v) => v !== numVal),
                    );
                  }}
                />
                <span className="text-slate-700">{opt.label}</span>
              </label>
            );
          })
        )}
      </div>
      <FieldErrorText error={error} />
    </div>
  );
};

export const FormGrid: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">{children}</div>
);
